/**
 * Service for API calls
 */

import httpClient from "../httpClient";
import { isValidPlaylistID } from "../utils/playlistID";

class ApiService {
  /**
   * Filter out deleted recordings from an array
   * @param {Array} recordings - Array of recordings
   * @returns {Array} - Filtered array without deleted recordings
   */
  filterDeletedRecordings(recordings) {
    if (!recordings || !Array.isArray(recordings)) {
      return recordings;
    }
    return recordings.filter(recording =>
      !recording.name || !recording.name.startsWith('[DELETED RECORDING')
    );
  }

  /**
   * Load a playlist by ID (a 7-char share ID or a vanity alias such as "@!!").
   * A playlist that does not exist rejects with an axios error whose
   * `response.status` is 404 — the store falls back to the public playlist on that.
   * @param {string} playlistID - The playlist ID to load
   * @returns {Promise} - Promise with the playlist data
   */
  async loadPlaylist(playlistID) {
    if (!isValidPlaylistID(playlistID)) {
      throw new Error("Invalid playlist ID");
    }

    const playlistURL = process.env.VUE_APP_BASE_URL + "/" + playlistID;

    try {
      const response = await httpClient.get(playlistURL, { retry: 10, retryDelay: 3500 });
      if (!response || !response.data || !response.data.recordings) {
        throw new Error("Unable to retrieve playlist");
      }
      return {
        recordings: this.filterDeletedRecordings(response.data.recordings),
        playlistURL
      };
    } catch (error) {
      console.error("Unable to retrieve playlist:", error);
      throw error;
    }
  }

  /**
   * Load the master playlist for a user
   * @param {string} userID - The user ID to load the playlist for
   * @returns {Promise} - Promise with the playlist data
   */
  async loadMasterPlaylist(userID) {
    if (!userID) {
      throw new Error("Invalid user ID");
    }

    try {
      const response = await httpClient.post(
        process.env.VUE_APP_BASE_URL,
        { userID },
        { retry: 10, retryDelay: 3500 }
      );

      if (!response || !response.data || !response.data.recordings) {
        throw new Error("Unable to retrieve playlist");
      }

      return {
        recordings: this.filterDeletedRecordings(response.data.recordings),
        isSuperAdmin: response.data.isSuperAdmin || false
      };
    } catch (error) {
      console.error("Unable to retrieve playlist:", error);
      throw error;
    }
  }

  /**
   * Create a new playlist
   * @param {string} creatorID - The ID of the user creating the playlist
   * @param {Array} recordingIDs - Array of recording IDs to include in the playlist
   * @returns {Promise} - Promise with the new playlist data
   */
  async createPlaylist(creatorID, recordingIDs) {
    if (!creatorID || !recordingIDs || recordingIDs.length === 0) {
      throw new Error("Invalid playlist creation parameters");
    }

    const createPlaylistURL = process.env.VUE_APP_BASE_URL + "/playlist/create";
    
    try {
      const response = await httpClient.post(
        createPlaylistURL, 
        {
          creatorID,
          recordings: recordingIDs,
        }, 
        { retry: 10, retryDelay: 3500 }
      );
      
      return {
        playlistID: response.data.playlistID,
        playlistURL: "https://platinumcouchproductions.com/" + response.data.playlistID
      };
    } catch (error) {
      console.error("Problem creating playlist:", error);
      throw error;
    }
  }

  // ── Poll feature ──

  /**
   * Fetch the currently active poll (and the caller's has_voted flag).
   * @returns {Promise<Object|null>}
   */
  async getActivePoll() {
    const response = await httpClient.get(
      process.env.VUE_APP_BASE_URL + "/poll/active",
      { retry: 5, retryDelay: 2000 }
    );
    return (response.data && response.data.poll) || null;
  }

  /**
   * Toggle a single (recording, option) vote on the active poll (live editing).
   * @param {number} pollId
   * @param {number} recordingId
   * @param {number} optionId
   * @param {boolean} checked
   */
  async setPollVote(pollId, recordingId, optionId, checked) {
    const response = await httpClient.post(
      process.env.VUE_APP_BASE_URL + "/poll/vote",
      { pollId, recordingId, optionId, checked }
    );
    return response.data;
  }

  /**
   * Fetch poll results by share hash (logged-in users only).
   * @param {string} shareHash
   * @returns {Promise<Object|null>}
   */
  async getPollResults(shareHash) {
    const response = await httpClient.get(
      process.env.VUE_APP_BASE_URL + "/poll/results/" + shareHash,
      { retry: 5, retryDelay: 2000 }
    );
    return (response.data && response.data.results) || null;
  }

  /**
   * List all polls (super admin only).
   * @returns {Promise<Array>}
   */
  async listPolls() {
    const response = await httpClient.get(process.env.VUE_APP_BASE_URL + "/poll/list");
    return (response.data && response.data.polls) || [];
  }

  /**
   * Create a poll (super admin only).
   * @param {string} title
   * @param {Array<string>} options
   */
  async createPoll(title, options) {
    const response = await httpClient.post(
      process.env.VUE_APP_BASE_URL + "/poll/create",
      { title, options }
    );
    return response.data;
  }

  /**
   * Set a poll's status (super admin only).
   * @param {number} pollId
   * @param {'draft'|'active'|'closed'} status
   */
  async setPollStatus(pollId, status) {
    const response = await httpClient.post(
      process.env.VUE_APP_BASE_URL + "/poll/" + pollId + "/status",
      { status }
    );
    return response.data;
  }

  /**
   * Create (or fetch) the shareable results link for a poll (super admin only).
   * @param {number} pollId
   */
  async createPollResultsLink(pollId) {
    const response = await httpClient.post(
      process.env.VUE_APP_BASE_URL + "/poll/" + pollId + "/results-link",
      {}
    );
    return response.data;
  }

  /**
   * Soft-delete a poll (super admin only).
   * @param {number} pollId
   */
  async deletePoll(pollId) {
    const response = await httpClient.delete(
      process.env.VUE_APP_BASE_URL + "/poll/" + pollId
    );
    return response.data;
  }

  // ── Track comments ──

  /**
   * Fetch the current user's own comment for a track (or null).
   * @param {number} recordingId
   * @returns {Promise<Object|null>} { body, updated_at } or null
   */
  async getMyComment(recordingId) {
    const response = await httpClient.get(
      process.env.VUE_APP_BASE_URL + "/comments/recording/" + recordingId + "/mine",
      { retry: 5, retryDelay: 2000 }
    );
    return (response.data && response.data.comment) || null;
  }

  /**
   * Upsert the current user's comment for a track (a blank body deletes it).
   * @param {number} recordingId
   * @param {string} body
   * @returns {Promise<Object>} { status, deleted, updated_at }
   */
  async setMyComment(recordingId, body) {
    const response = await httpClient.put(
      process.env.VUE_APP_BASE_URL + "/comments/recording/" + recordingId + "/mine",
      { body }
    );
    return response.data;
  }

  /**
   * Fetch the anonymous comment thread for a track (everyone's comments).
   * @param {number} recordingId
   * @returns {Promise<Array>} [{ body, updated_at }]
   */
  async getTrackThread(recordingId) {
    const response = await httpClient.get(
      process.env.VUE_APP_BASE_URL + "/comments/recording/" + recordingId + "/thread",
      { retry: 5, retryDelay: 2000 }
    );
    return (response.data && response.data.thread) || [];
  }

  /**
   * Fetch the recording ids the current user has commented on (drives the icon state).
   * @returns {Promise<Array<number>>}
   */
  async getMyCommentedRecordings() {
    const response = await httpClient.get(
      process.env.VUE_APP_BASE_URL + "/comments/mine"
    );
    return (response.data && response.data.recordingIds) || [];
  }

  /**
   * Categorize an upload error into a user-friendly message.
   * @param {Error} error - The error to categorize
   * @returns {string} - User-friendly error message
   */
  categorizeUploadError(error) {
    const message = error.message || '';
    const status = error.response?.status || error.status;

    if (message === 'Network error during upload' || message === 'Network Error') {
      return 'Network error — please check your connection and try again';
    }
    if (message === 'Upload aborted' || message.includes('timeout')) {
      return 'Upload timed out — the file may be too large for your current connection';
    }
    if (status === 409 || message.includes('already exists')) {
      return 'A track with this name already exists';
    }
    if (status === 404 && message.includes('not found in storage')) {
      return 'Files could not be verified in storage — please retry the upload';
    }
    if (status >= 500 || message.includes('Server error')) {
      return 'Server error — please try again in a few minutes';
    }
    return message || 'Upload failed';
  }

  /**
   * Clean up S3 files after a failed upload.
   * @param {string} fileName - The track name
   * @param {string} highQualityExt - The high quality file extension
   * @param {string} regularQualityExt - The regular quality file extension
   * @returns {Promise<boolean>} - true if cleanup succeeded
   */
  async cleanupUpload(fileName, highQualityExt, regularQualityExt) {
    try {
      await httpClient.post(
        process.env.VUE_APP_BASE_URL + "/cleanup-upload",
        { fileName, highQualityExt, regularQualityExt }
      );
      return true;
    } catch (cleanupError) {
      console.error("Cleanup failed:", cleanupError);
      return false;
    }
  }

  /**
   * Upload audio files using presigned URLs for direct S3 upload
   * @param {Object} fileInfo - Information about the files being uploaded
   * @param {File} highQualityFile - High quality audio file
   * @param {File} regularQualityFile - Regular quality audio file
   * @param {Function} onProgress - Optional callback for upload progress
   * @returns {Promise} - Promise with the upload result
   */
  async uploadFiles(fileInfo, highQualityFile, regularQualityFile, onProgress) {
    if (!highQualityFile || !regularQualityFile || !fileInfo || !fileInfo.title) {
      throw new Error("Invalid upload parameters");
    }

    return new Promise((resolve, reject) => {
      this.getAudioFileDuration(regularQualityFile, async (duration) => {
        const highQualityExt = '.' + highQualityFile.name.split('.').pop().toLowerCase();
        const regularQualityExt = '.' + regularQualityFile.name.split('.').pop().toLowerCase();
        let highQualityUploaded = false;
        let regularQualityUploaded = false;

        try {
          // Step 1: Get presigned URLs from backend
          if (onProgress) onProgress('status', 'Requesting upload URLs...');
          const presignedResponse = await httpClient.post(
            process.env.VUE_APP_BASE_URL + "/get-upload-urls",
            {
              fileName: fileInfo.title,
              highQualityExt,
              regularQualityExt
            }
          );

          if (presignedResponse.data.status !== 'success') {
            throw new Error('Failed to get presigned URLs');
          }

          const { highQualityUrl, regularQualityUrl, highQualityKey, regularQualityKey } = presignedResponse.data;

          // Step 2: Upload files directly to S3 using presigned URLs
          if (onProgress) onProgress('status', 'Uploading high quality file...');
          const highQualityUpload = await this.uploadToS3WithRetry(
            highQualityUrl,
            highQualityFile,
            (progress) => {
              if (onProgress) onProgress('highQuality', progress);
            }
          );

          if (!highQualityUpload.ok) {
            throw new Error(`High quality upload failed: ${highQualityUpload.status}`);
          }
          highQualityUploaded = true;

          if (onProgress) onProgress('status', 'Uploading MP3 file...');
          const regularQualityUpload = await this.uploadToS3WithRetry(
            regularQualityUrl,
            regularQualityFile,
            (progress) => {
              if (onProgress) onProgress('regularQuality', progress);
            }
          );

          if (!regularQualityUpload.ok) {
            throw new Error(`Regular quality upload failed: ${regularQualityUpload.status}`);
          }
          regularQualityUploaded = true;

          // Step 3: Save metadata to backend (with S3 keys for verification)
          if (onProgress) onProgress('status', 'Saving metadata...');
          const metadataResponse = await httpClient.post(
            process.env.VUE_APP_BASE_URL + "/save-recording-metadata",
            {
              name: fileInfo.title,
              date: fileInfo.date,
              duration: duration,
              misc: fileInfo.misc || null,
              highQualityKey,
              regularQualityKey
            }
          );

          if (metadataResponse.data.status === 'success') {
            resolve(metadataResponse.data);
          } else if (metadataResponse.data.status === 'already_exists') {
            // Track already exists — clean up the S3 files we just uploaded
            await this.cleanupUpload(fileInfo.title, highQualityExt, regularQualityExt);
            const error = new Error(metadataResponse.data.message);
            error.status = 409;
            throw error;
          } else {
            throw new Error('Failed to save metadata');
          }
        } catch (error) {
          // Clean up any S3 files that were uploaded before the failure
          if (highQualityUploaded || regularQualityUploaded) {
            const cleanedUp = await this.cleanupUpload(fileInfo.title, highQualityExt, regularQualityExt);
            if (cleanedUp) {
              error.cleanedUp = true;
            }
          }

          // Categorize the error for user-friendly display
          error.userMessage = this.categorizeUploadError(error);
          reject(error);
        }
      });
    });
  }

  /**
   * Upload a file to S3 with retry logic (exponential backoff).
   * Retries on network errors and 5xx responses. Does not retry 4xx.
   * @param {string} presignedUrl - The presigned URL for upload
   * @param {File} file - The file to upload
   * @param {Function} onProgress - Optional callback for upload progress
   * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
   * @returns {Promise<Object>} - The upload result {ok, status, statusText}
   */
  async uploadToS3WithRetry(presignedUrl, file, onProgress, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.uploadToS3(presignedUrl, file, onProgress);

        // Don't retry on success or client errors (4xx)
        if (result.ok || (result.status >= 400 && result.status < 500)) {
          return result;
        }

        // Server error (5xx) — retry
        lastError = new Error(`Upload failed with status ${result.status}`);
        lastError.status = result.status;
      } catch (error) {
        lastError = error;
        // Network errors are retryable; abort is not
        if (error.message === 'Upload aborted') {
          throw error;
        }
      }

      if (attempt < maxRetries) {
        const delay = Math.pow(3, attempt - 1) * 1000; // 1s, 3s, 9s
        await new Promise(resolve => setTimeout(resolve, delay));
        // Reset progress for retry
        if (onProgress) onProgress(0);
      }
    }

    throw lastError;
  }

  /**
   * Upload a file directly to S3 using a presigned URL
   * @param {string} presignedUrl - The presigned URL for upload
   * @param {File} file - The file to upload
   * @param {Function} onProgress - Optional callback for upload progress
   * @returns {Promise<Object>} - {ok, status, statusText}
   */
  async uploadToS3(presignedUrl, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText
        });
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  /**
   * Soft-delete a recording (super admin only)
   * @param {number} recordingId - The ID of the recording to delete
   * @param {string} userID - The Firebase UID of the super admin
   * @returns {Promise} - Promise with the deletion result
   */
  async deleteRecording(recordingId, userID) {
    if (!recordingId || !userID) {
      throw new Error("Invalid delete parameters");
    }

    try {
      const response = await httpClient.delete(
        process.env.VUE_APP_BASE_URL + "/recording/delete",
        { data: { id: recordingId, userID } }
      );

      return response.data;
    } catch (error) {
      console.error("Error deleting recording:", error);
      throw error;
    }
  }

  /**
   * Increment the number of listens for a recording
   * @param {string} recordingID - The ID of the recording to increment
   * @returns {Promise} - Promise with the updated listen count
   */
  async incrementNumberOfListens(recordingID) {
    if (!recordingID) {
      throw new Error("Invalid recording ID");
    }

    const incrementNumberOfListensURL = 
      process.env.VUE_APP_BASE_URL + "/recording/incrementNumberOfListens";

    try {
      const response = await httpClient.post(
        incrementNumberOfListensURL, 
        { id: recordingID }, 
        { retry: 10, retryDelay: 3500 }
      );
      
      return {
        recordingID, 
        updatedNumberOfListens: response.updatedNumberOfListens
      };
    } catch (error) {
      console.error("Problem incrementing number of listens:", error);
      throw error;
    }
  }

  /**
   * Get the duration of an audio file
   * @param {File} file - The audio file to get the duration of
   * @param {Function} callback - Callback with the duration
   */
  getAudioFileDuration(file, callback) {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = (e) => {
      const ctx = new AudioContext();
      const audioArrayBuffer = e.target.result;
      ctx.decodeAudioData(audioArrayBuffer, data => {
        callback(this.formatTime(data.duration));
      }, error => {
        console.error("Error getting audio duration:", error);
        callback("0:00");
      });
    };
  }

  /**
   * Format seconds into MM:SS format
   * @param {number} numberOfSeconds - Number of seconds to format
   * @returns {string} - Formatted time string
   */
  formatTime(numberOfSeconds) {
    var date = new Date(0);
    date.setSeconds(numberOfSeconds);
    var timeString = date.toISOString().substring(14, 19);
    return timeString;
  }
}

export default new ApiService();